/**
 * Photo-grading constants — mirrored from
 * apps/backend/core/constants.py::GradingConfig (PRD-20).
 * When you change one side, change the other in the same PR.
 */

import { MARKUP_LANGUAGE } from "./programming"

// Image count + size envelope. PRD-20 §FR-5 / §FR-6.
export const GRADING_MAX_IMAGES = 3
export const GRADING_MAX_TOTAL_BYTES = 5 * 1024 * 1024

// MIME allowlist for the grading upload endpoint. Superset of the
// OCR-verification allowlist: also accepts `application/pdf`. The
// Celery task branches: .pdf → pypdf text-layer extract; otherwise
// → Vision OCR. Mirrors apps/backend/core/constants.py::GradingConfig.
export const GRADING_MIME_ALLOWLIST = [
  "image/jpeg",
  "image/png",
  "image/heic",
  "image/heif",
  "application/pdf",
] as const
export type GradingMime = (typeof GRADING_MIME_ALLOWLIST)[number]

// Image-only subset, used by the "Tomar foto" capture button — the
// camera shouldn't return a PDF.
export const GRADING_CAMERA_MIME_ALLOWLIST = [
  "image/jpeg",
  "image/png",
  "image/heic",
  "image/heif",
] as const

// Auto-accept gate (PRD-20 §FR-17). When every question outcome is
// `correct` AND every per-question confidence is at least this value,
// the review screen surfaces a one-tap "Aceptar y guardar" CTA.
export const GRADING_AUTO_ACCEPT_MIN_CONFIDENCE = 0.9

// Per-question outcome buckets — wire format from ADR-0023.
// PRD-43 §FR-2.5 keeps this enum UNCHANGED: the judge's verdict maps onto
// it (`equivalent → correct`, `not_equivalent → incorrect`,
// `blank`/`unreadable` → `no_match`) and the PATCH still validates against
// these four values server-side.
export const GRADING_OUTCOMES = ["correct", "probable_match", "incorrect", "no_match"] as const
export type GradingOutcome = (typeof GRADING_OUTCOMES)[number]

// Per-question judge verdicts (PRD-43 §FR-1.4). Canonical source is the
// backend's `responseSchema` for the VLM judge call — see
// `apps/backend/core/constants.py::GradingConfig` and the prompt Anexo in
// `docs/prd/43-vlm-grading-judge.md`. Change one side, change the other in
// the same PR.
//
// A verdict never replaces `outcome` — it refines the copy the professor
// reads. `blank` ("no contestó") and `unreadable` ("no pudimos leerlo")
// both land on `no_match`, and telling them apart is the whole point: they
// imply opposite next actions for the professor.
export const GRADING_VERDICTS = ["equivalent", "not_equivalent", "blank", "unreadable"] as const
export type GradingVerdict = (typeof GRADING_VERDICTS)[number]

// PRD-43 §FR-6.1 / §FR-5.3 — the two ways an upload reaches the professor's
// queue with NOTHING graded: the judge was unreachable, or the material has
// no answer key to cotejar against. Mirrors the `reason` values passed to
// `apps/backend/core/tasks.py::_finalize_pending_review`, which is also what
// lands on `ProgressUpload.error_code`.
//
// Neither is a failure of the submission — the photos are fine and the
// professor grades them by hand — so every review surface has to be able to
// say WHICH of the two happened. Declared once here because three call sites
// branch on them (the reasons list, the empty questions state, the review
// panel notice) and a fourth spelling would silently render the generic copy.
// PRD-50 adds three more, for `codigo` submissions (mirrors
// `apps/backend/core/constants.py::GradingConfig.reason_code_*`):
// a paper (photo/PDF) submission is never OCR'd (§9.2), an empty text
// file has nothing to evaluate, and a lost eval callback is degraded by
// the review queue's zombie sweep (§9.3). All three land the upload in
// review with `questions: []`, same as the two judge-era codes.
// PRD-67 adds the two where the reader found NOTHING TO GRADE (mirrors
// `GradingConfig.reason_not_an_answer_sheet` / `reason_answer_key_uploaded`):
// the images are not this material's sheet, or they are the professor's
// own answer key. Unlike the others these carry what the reader saw
// (`images[].raw_text`, `submission_check.reason`), because "why is there
// no score?" is answered by the photos, not by an outage.
export const GRADING_DEGRADE_CODES = {
  vlmUnavailable: "vlm_unavailable",
  noAnswerKey: "answer_key_missing",
  codePaperSubmission: "code_paper_submission",
  codeEvalUnavailable: "code_eval_unavailable",
  codeSubmissionEmpty: "code_submission_empty",
  notAnAnswerSheet: "not_an_answer_sheet",
  answerKeyUploaded: "ocr_grading_answer_key_scanned",
} as const
export type GradingDegradeCode = (typeof GRADING_DEGRADE_CODES)[keyof typeof GRADING_DEGRADE_CODES]

/** PRD-67 — the two degrade codes where the reader found nothing to grade. */
export const NOTHING_TO_GRADE_CODES: readonly string[] = [
  GRADING_DEGRADE_CODES.notAnAnswerSheet,
  GRADING_DEGRADE_CODES.answerKeyUploaded,
]

// PRD-67 — upload-level flags for what the reader's own output could not
// vouch for. Mirrors `apps/backend/core/constants.py::GradingConfig.flag_*`
// (`ocr_metadata.review_flags`, and `review_flags` on the queue row). A
// flagged draft is never pre-filled into the score box: the 2026-10-15
// incident (stock photos drafted 10/10) was one Enter away from the
// gradebook because the number was already typed.
export const GRADING_REVIEW_FLAGS = {
  answerSheetUnsure: "answer_sheet_unsure",
  // PRD-67 §FR-3.6 — blanks the reader cannot back: questions it never
  // found, or a page it never read (2026-10-30, a 10/10 drafted as 4/10).
  incompleteReading: "incomplete_reading",
  evidenceMissing: "evidence_missing",
  perfectWithoutEvidence: "perfect_without_evidence",
} as const
export type GradingReviewFlag = (typeof GRADING_REVIEW_FLAGS)[keyof typeof GRADING_REVIEW_FLAGS]

// PRD-59 — the ONLY scan failures that justify throwing the professor's
// photographs away. Mirrors the `_finalize_failure` call sites in
// `apps/backend/core/tasks.py::auto_grade_submission`; change one side,
// change the other in the same PR.
//
// Keyed on the entity the loss originates from (`guard-at-the-cause.md` §1):
// the question is whether THE CAPTURE is unusable, not whether the run
// failed. A wrong paper (the professor's own answer key) and an upload that
// arrived with no images are the two cases where the held files are worth
// nothing — a new photograph is the only way forward. Every other failure —
// an unread header, an ambiguous roster, a Vision timeout, a cell that
// already had a grade — is a question about WHO or WHEN, and the photographs
// are still the evidence. The professor is standing at a desk with a stack
// that has already moved on; discarding them blanket-style is unrecoverable,
// and it contradicted `errors:ocr_grading_student_unidentified`, which tells
// her to "elige al alumno manualmente".
//
// Closed and small on purpose: keeping the files is never destructive, so
// the safe default is to keep, and only a named code discards.
export const SCAN_RECAPTURE_ERROR_CODES = [
  "ocr_grading_answer_key_scanned",
  "ocr_grading_no_images",
] as const

export const isScanRecaptureCode = (code: string | null | undefined): boolean =>
  !!code && (SCAN_RECAPTURE_ERROR_CODES as readonly string[]).includes(code)

// PRD-50 — what the student actually HANDED IN for a `codigo` exercise.
//
// Four of the five exercise types (`implement_function`, `debug_code`,
// `complete_code`, `mixed`) end with the student writing code, so the
// review copy talks about functions and test cases. `predict_output` does
// not: the student reads a program and writes down what it PRINTS. Telling
// that professor "no encontramos esta función en la entrega" describes a
// submission nobody asked for.
//
// Collapsing five exercise types into two submission shapes is deliberate —
// the copy only ever needs to answer "is this code or is this an output?",
// and a per-type copy table would be four identical columns and one
// different one. Unknown / absent types (rows written before
// `exercise_type` existed) read as `code`, which is the pre-existing
// behaviour and correct for every language shipped so far.
//
// PRD-72 adds the third shape, and it is decided by the LANGUAGE, not the
// exercise type: an `html_css` exercise is a section of one page, checked
// against requirements, so "función" and "casos" describe nothing the
// student handed in. Every exercise type of that language is a page.
export const CODE_SUBMISSION_SHAPES = ["code", "output", "page"] as const
export type CodeSubmissionShape = (typeof CODE_SUBMISSION_SHAPES)[number]

export const codeSubmissionShape = (
  exerciseType: string | null | undefined,
  // Required on purpose: forgetting it would word a page as a function.
  language: string | null | undefined,
): CodeSubmissionShape => {
  if (language === MARKUP_LANGUAGE) return "page"
  return exerciseType === "predict_output" ? "output" : "code"
}

// Realtime phase event names on `grade.phase`. Mirrors PRD-19 OCR
// but with two extra intermediate phases (`cotejando` / `calificando`)
// because the grading pipeline does more than just OCR.
export const GRADING_PHASES = [
  "subiendo",
  "leyendo",
  "cotejando",
  "calificando",
  "listo",
  "fallo",
] as const
export type GradingPhase = (typeof GRADING_PHASES)[number]

// The four phases that have a visible step in the progress bar.
// `subiendo` is shown only while the upload is in flight (frontend-only);
// `listo` / `fallo` flip the bar to a terminal state.
export const GRADING_VISIBLE_PHASES = ["subiendo", "leyendo", "cotejando", "calificando"] as const

// Allowed accept paths on the PATCH save payload — backend rejects
// anything else with `progress_upload_invalid_type`. PRD-20 v1.4
// §FR-58 extended the set with `manual_review_required` (written by
// the Celery task on the strict-gate fail) and `manual_override` (used
// by the cell editor when editing a previously committed grade).
//
// SINGLE SOURCE. `@/types` re-exports this; it used to declare a second,
// four-member copy of the same union, so a value written by the task
// (`auto_on_success`) type-checked in one file and not the other.
export type GradingAcceptPath =
  | "auto"
  | "auto_on_success"
  | "auto_on_success_backfill"
  | "manual_review"
  | "manual_review_required"
  | "manual_override"
  // PRD-69 — written only by the admin staff tool; a professor's save never
  // sends them, and the golden set never counts them.
  | "staff_review"
  | "staff_override"
